import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { ArrowUpRight, ArrowDown } from "lucide-react";

const HERO_BG = "/media/hero-breitscheidplatz.jpg";

export default function Hero() {
  const { t, lang } = useLang();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative md:h-screen md:min-h-[820px] w-full overflow-hidden bg-[#141712]"
    >
      {/* Bild: mobil volle Breite im natürlichen Format, ab md fullscreen */}
      <div className="relative w-full pt-16 md:pt-0 md:absolute md:inset-0">
        <img
          src={HERO_BG}
          alt="Visualisierung: Berlin-Cube am Breitscheidplatz — begrünte Sitzmodule vor der Gedächtniskirche"
          srcSet="/media/hero-breitscheidplatz-mobile.jpg 960w, /media/hero-breitscheidplatz.jpg 1647w"
          sizes="100vw"
          className="w-full h-auto md:h-full md:absolute md:top-0 md:bottom-0 md:right-0 md:left-0 md:object-cover md:object-right min-[1200px]:max-[1700px]:left-auto min-[1200px]:max-[1700px]:w-[118%] min-[1200px]:max-[1700px]:max-w-none"
        />
        <div className="hidden md:block absolute inset-0 bg-gradient-to-b from-[#1F2328]/85 via-[#1F2328]/25 to-transparent" />
        <div className="hidden md:block absolute inset-0 bg-gradient-to-r from-[#1F2328]/70 via-transparent to-transparent" />

        {/* Badge · Visualisierung (gleiche Optik wie HEUTE · FOTO in Realisiert) */}
        <div
          data-testid="hero-visualisierung-badge"
          className="absolute bottom-3 left-3 md:bottom-auto md:left-auto md:top-28 md:right-14 z-10 inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-white/85 bg-black/50 backdrop-blur px-3 py-1.5 border border-white/10 font-display"
        >
          <span className="h-1 w-1 rounded-full bg-white/70" />
          {lang === "de" ? "Visualisierung" : "Visualisation"}
        </div>
      </div>

      {/* Positioning strap (top) — mobil ausgeblendet */}
      <div className="hidden md:block absolute top-28 left-14 z-10">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="flex items-center gap-3 text-[11px] tracking-[0.35em] text-white/80 uppercase font-display"
        >
          <span className="h-px w-8 bg-[#F5D77A]" />
          {t.hero.positioning}
        </motion.div>
      </div>

      {/* Badge alt-Position entfernt — jetzt im Bild-Wrapper */}

      {/* Content — mobil unter dem Bild auf #141712, ab md links oben über dem Blätterdach */}
      <div className="relative z-10 pt-10 pb-2 md:pb-0 md:absolute md:inset-x-0 md:top-0 md:pt-48">
        <div className="max-w-[1400px] mx-auto px-6 md:px-14">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8 lg:pl-[22%]">
              {/* BERLIN — line — CUBE · semantische H1, innerText/textContent = "BERLIN-CUBE — …" */}
              <h1
                translate="no"
                className="notranslate font-display font-light leading-[0] tracking-[-0.035em] text-white"
                style={{ fontSize: "clamp(3.5rem, 11vw, 10rem)" }}
                data-testid="hero-title"
              >
                <motion.span
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-block w-full align-top leading-[0.85]"
                >
                  {t.hero.brandLine1}
                </motion.span>
                <span className="inline-block w-0 h-0 overflow-hidden align-top">-</span>
                <span className="inline-block w-full align-top leading-[0]" aria-hidden>
                  <motion.span
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: "38%", opacity: 1 }}
                    transition={{ delay: 1.05, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                    className="inline-block align-top h-[0.14em] my-2 md:my-4"
                    style={{ background: "#F5D77A" }}
                  />
                </span>
                <motion.span
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.1, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-block w-full align-top leading-[0.85] font-semibold"
                  style={{ color: "#F5D77A" }}
                >
                  {t.hero.brandLine2}
                </motion.span>
                <span className="inline-block w-0 h-0 overflow-hidden align-top whitespace-pre"> — {t.hero.h1Rest}</span>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.6, duration: 0.9 }}
                className="mt-8 font-display text-white text-xl md:text-2xl tracking-tight"
                data-testid="hero-claim"
              >
                {t.hero.claim}
              </motion.p>

              {t.hero.claimDe && (
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.7, duration: 0.9 }}
                  className="mt-3 hidden md:block font-display italic text-[#F5D77A]/85 text-base md:text-lg tracking-tight"
                  data-testid="hero-claim-de"
                >
                  {t.hero.claimDe}
                </motion.p>
              )}

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.75, duration: 0.9 }}
                className="mt-4 max-w-2xl text-white/85 text-base md:text-[15px] leading-relaxed"
                data-testid="hero-lead-spaces"
              >
                {t.hero.leadSpaces}
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.8, duration: 0.9 }}
                className="mt-2 max-w-2xl text-white/60 text-base md:text-[15px] leading-relaxed"
              >
                {t.hero.lead}
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3, duration: 0.9 }}
              className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end"
            >
              <a
                href="#change"
                data-testid="hero-cta-primary"
                className="group inline-flex w-full md:w-auto lg:w-auto items-center justify-between gap-6 px-6 py-4 bg-[#F5D77A] text-[#1F2328] font-medium tracking-wide hover:bg-white transition-colors"
              >
                <span>{t.hero.cta1}</span>
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/pitch.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-secondary"
                className="group inline-flex w-full md:w-auto lg:w-auto items-center justify-between gap-6 px-6 py-4 border border-white/30 text-white hover:bg-white hover:text-[#1F2328] transition-colors backdrop-blur-sm"
              >
                <span>{t.hero.cta2}</span>
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </motion.div>
          </div>

          <div className="mt-14 flex items-end justify-between border-t border-white/10 pt-5">
            <div className="flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-white/50">
              <ArrowDown className="h-3.5 w-3.5" /> {t.hero.scroll}
            </div>
            <div className="hidden md:flex items-center gap-8 text-[11px] tracking-[0.28em] uppercase text-white/50">
              <span>DEKRA IWA 14-1 N2A · Gen 1</span>
              <span className="text-white/25">·</span>
              <span>EU PATENT EP3486374</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
